/**
 * Audio adapter. The sim emits events; this drains them and plays sounds.
 *
 * Synthesized via Web Audio API (no asset files, keeps the bundle tiny).
 * The synth lives behind a tiny interface so tests can replace it with a
 * mock and assert "this event was played, that volume was set".
 *
 * Web Audio contexts must be created or resumed from a user gesture
 * (browser autoplay policy), so we initialize lazily on first event.
 */

import type { GameEvent } from '../sim/types.ts';

export type Synth = {
  play(kind: GameEvent['kind'], payload?: Record<string, unknown>): void;
  setVolume(v: number): void;
  /** Resume the audio context from a user gesture. */
  resume(): Promise<void>;
};

export type AudioAdapter = {
  consume(events: readonly GameEvent[]): void;
  setVolume(v: number): void;
  resume(): Promise<void>;
};

/**
 * Production synth using Web Audio. Plays distinct timbres per event kind.
 * Lazy: `AudioContext` is constructed on first use.
 */
export function createWebAudioSynth(): Synth {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Ctor = (globalThis as any).AudioContext ?? (globalThis as any).webkitAudioContext;
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let volume = 0.5;

  function ensure(): { ctx: AudioContext; master: GainNode } | null {
    if (typeof Ctor !== 'function') return null;
    if (!ctx) {
      const c = new Ctor() as AudioContext;
      const m = c.createGain();
      m.gain.value = volume;
      m.connect(c.destination);
      ctx = c;
      master = m;
    }
    return { ctx: ctx as AudioContext, master: master as GainNode };
  }

  function blip(freq: number, duration: number, type: OscillatorType = 'square'): void {
    const built = ensure();
    if (!built) return;
    const { ctx: c, master: m } = built;
    const osc = c.createOscillator();
    const env = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, c.currentTime);
    env.gain.setValueAtTime(0, c.currentTime);
    env.gain.linearRampToValueAtTime(1.0, c.currentTime + 0.005);
    env.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);
    osc.connect(env);
    env.connect(m);
    osc.start();
    osc.stop(c.currentTime + duration + 0.02);
  }

  function noise(duration: number, freq = 1000): void {
    const built = ensure();
    if (!built) return;
    const { ctx: c, master: m } = built;
    const bufferSize = Math.floor(c.sampleRate * duration);
    const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    const src = c.createBufferSource();
    src.buffer = buffer;
    const filter = c.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = freq;
    const env = c.createGain();
    env.gain.value = 0.4;
    src.connect(filter);
    filter.connect(env);
    env.connect(m);
    src.start();
  }

  function arpeggio(freqs: number[], step = 0.06): void {
    const built = ensure();
    if (!built) return;
    const { ctx: c } = built;
    let when = c.currentTime;
    for (const f of freqs) {
      setTimeout(() => blip(f, 0.15, 'triangle'), Math.round((when - c.currentTime) * 1000));
      when += step;
    }
  }

  return {
    play(kind, payload) {
      switch (kind) {
        case 'flag_pickup': {
          const flagKind = payload?.flagKind as string | undefined;
          switch (flagKind) {
            case 'doubler':
              blip(880, 0.12, 'triangle');
              break;
            case 'fuel_bonus':
              arpeggio([523.25, 783.99], 0.05);
              break;
            case 'speed_boost':
              blip(440, 0.08, 'sawtooth');
              setTimeout(() => blip(660, 0.08, 'sawtooth'), 60);
              break;
            default:
              blip(660, 0.10, 'square');
          }
          break;
        }
        case 'smoke_deploy':
          noise(0.18, 700);
          break;
        case 'enemy_stun':
          blip(140, 0.18, 'sawtooth');
          break;
        case 'player_die':
          arpeggio([440, 330, 220, 110], 0.08);
          break;
        case 'extra_life':
          arpeggio([523.25, 659.25, 783.99, 1046.5], 0.06);
          break;
        case 'stage_clear':
          arpeggio([523.25, 659.25, 783.99, 1046.5, 1318.51], 0.07);
          break;
        case 'game_over':
          arpeggio([392, 311, 247, 196, 156], 0.12);
          break;
      }
    },
    setVolume(v) {
      volume = Math.max(0, Math.min(1, v));
      const built = ensure();
      if (built) built.master.gain.value = volume;
    },
    async resume() {
      const built = ensure();
      if (built && built.ctx.state === 'suspended') await built.ctx.resume();
    },
  };
}

/** Builds an AudioAdapter around any Synth implementation. */
export function createAudioAdapter(synth: Synth): AudioAdapter {
  return {
    consume(events) {
      for (const ev of events) {
        if (ev.kind === 'flag_pickup') synth.play(ev.kind, { flagKind: ev.flagKind });
        else synth.play(ev.kind);
      }
    },
    setVolume: synth.setVolume.bind(synth),
    resume: synth.resume.bind(synth),
  };
}
